'use client';

import { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { Dialog } from '@packages/shared/src/ui/Dialog/Dialog';
import { DialogContent } from '@packages/shared/src/ui/Dialog/DialogContent/DialogContent';
import { DialogHeader } from '@packages/modules/src/Dialog/DialogHeader/DialogHeader.client';
import { useConfig } from '@packages/shared/src/hooks/useConfig/useConfig';
import { type CliplisterQueryVariables } from '@packages/gql/generated/shopping/graphql';
import { useCookieConsent } from '@packages/shared/src/hooks/useCookieConsent/useCookieConsent';
import { dispatchDisplayOverlayTracking } from '@packages/shared/src/ui/Dialog/dispatchDialogOverlayTracking';
import { useTracking } from '@packages/tracking/src/hooks/useTracking/useTracking';
import { CliplisterVideo } from './CliplisterVideo/CliplisterVideo.client';
import { ImageGalleryCallToActionButton } from '../ImageGalleryCallToActionButton/ImageGalleryCallToActionButton.client';
import { addCliplisterScript } from './addCliplisterScript/addCliplisterScript';
import { useVideoTracking } from '../Tracking/hooks/useVideoTracking';

export type CliplisterProps = Pick<CliplisterQueryVariables, 'orderNumber'>;

/**
 * <p>
 * This component is used to show product videos from cliplister.
 * </p>
 * <p>
 * It originally shows an icon. After it is clicked a `Dialog` opens, which shows a product preview image.
 * After the image is clicked, the player is appended and starts playing the product video.
 *
 * This components needs to have Cookie Consent 4 (marketing) in order to be displayed.
 * <a href="/story/docs-reference-storybook-tips-and-tricks--docs">Here</a> you can see how you can simulate the consent in storybook.
 * </p>
 *
 */

export const Cliplister = ({ orderNumber }: CliplisterProps) => {
  const dispatchGTMEvent = useTracking();
  const {
    product: {
      cliplister: { scriptUrl, consentGroup, enabled },
    },
  } = useConfig();
  // scoped two-way rendering to avoid hydration errors because of consent cookie dependecy
  const [rendered, setRendered] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [scriptLoaded, setScriptloaded] = useState(false);

  const isCookieAllowed = useCookieConsent(consentGroup);

  const { formatMessage } = useIntl();
  const { trackGlycerinDisplayVideo, trackVideoIconClick } = useVideoTracking();

  const isCliplisterAllowed = rendered && enabled && isCookieAllowed;
  const isCliplisterAvailable = isCliplisterAllowed && scriptLoaded;

  useEffect(() => {
    setRendered(true);
  }, []);

  useEffect(() => {
    if (isCliplisterAllowed) {
      addCliplisterScript(scriptUrl)
        .then(() => setScriptloaded(true))
        .catch(() => setScriptloaded(false));
    }
  }, [isCliplisterAllowed, scriptUrl]);

  useEffect(() => {
    if (isCliplisterAvailable) {
      trackGlycerinDisplayVideo();
    }
  }, [isCliplisterAvailable, orderNumber, trackGlycerinDisplayVideo]);

  if (!isCliplisterAvailable) return null;

  const dialogTitle = formatMessage({
    id: 'cliplister.modal.title',
    defaultMessage: 'Produkt Video',
  });

  const openModalHandler = () => {
    trackVideoIconClick();
    setOpenDialog(true);
    dispatchDisplayOverlayTracking({
      eventDispatcher: dispatchGTMEvent,
      displayTrackingProps: {
        category: 'cliplister-dialog',
        label: dialogTitle,
      },
    });
  };

  return (
    <>
      <ImageGalleryCallToActionButton
        onClick={openModalHandler}
        buttonAriaLabel={formatMessage({
          id: 'cliplister.aria.button',
          defaultMessage: 'Produktvideo abspielen',
        })}
      >
        <span className="icon-emp-video text-color-text-dark size-4" />
      </ImageGalleryCallToActionButton>
      <Dialog
        className="w-full max-w-[600px]"
        isOpen={openDialog}
        onClose={() => {
          setOpenDialog(false);
        }}
      >
        <DialogHeader onClose={() => setOpenDialog(false)}>{dialogTitle}</DialogHeader>
        <DialogContent>
          <CliplisterVideo orderNumber={orderNumber} />
        </DialogContent>
      </Dialog>
    </>
  );
};
